import {z} from "zod";

/** Galactus schema version 1 review projection. The executable request stays on the server. */
export const mutationProposalSchema = z.object({
   schemaVersion: z.literal(1),
   proposalId: z.string().uuid(),
   capability: z.string().min(1),
   issuedAt: z.string().datetime({offset: true}),
   expiresAt: z.string().datetime({offset: true}),
   summary: z.string(),
   targetId: z.string().min(1),
   before: z.record(z.string(), z.unknown()),
   after: z.record(z.string(), z.unknown()),
   warnings: z.array(z.string()),
});
export type MutationProposal = z.infer<typeof mutationProposalSchema>;

/** Capabilities whose proposals are reviewed on the edited resource's own page. */
const PAGE_REVIEW_CAPABILITIES = ["phone_scripts_update", "use_cases_update"] as const;
export type PageReviewCapability = (typeof PAGE_REVIEW_CAPABILITIES)[number];
export const isPageReviewCapability = (capability: string): capability is PageReviewCapability =>
   (PAGE_REVIEW_CAPABILITIES as readonly string[]).includes(capability);

/** The resource a page-reviewable proposal edits. */
export type ProposalTarget = {capability: PageReviewCapability; targetId: string};

/** Paths and the API can spell the same target ID in different cases. */
export const sameProposalTarget = (left: {capability: string; targetId: string}, right: {capability: string; targetId: string}) =>
   left.capability === right.capability && left.targetId.toLowerCase() === right.targetId.toLowerCase();

export type GailApprovalPreview = {
   approvalId: string;
   toolCallId: string;
   proposal?: MutationProposal;
   previewUnavailable?: true;
};

/**
 * Read Wingman's `data-gailApproval` binding. Capabilities without a review projection
 * omit `proposal`; one that is present but unreadable is reported, never dropped, so the
 * approval cannot silently fall back to approving raw tool arguments.
 * A malformed binding aborts processing because its approval cannot be identified safely.
 */
export function readGailApproval(data: unknown): GailApprovalPreview {
   const binding = z.object({approvalId: z.string().min(1), toolCallId: z.string().min(1), proposal: z.unknown()}).safeParse(data);
   if (!binding.success) throw new Error("Invalid approval binding");
   const {approvalId, toolCallId, proposal} = binding.data;
   if (proposal === undefined) return {approvalId, toolCallId};
   const preview = mutationProposalSchema.safeParse(proposal);
   return preview.success ? {approvalId, toolCallId, proposal: preview.data} : {approvalId, toolCallId, previewUnavailable: true};
}

/** Object key order is not a script change; array order is meaningful. */
export function snapshotValuesEqual(left: unknown, right: unknown): boolean {
   if (Object.is(left, right)) return true;
   if (Array.isArray(left) && Array.isArray(right)) return left.length === right.length && left.every((item, index) => snapshotValuesEqual(item, right[index]));
   if (!left || !right || typeof left !== "object" || typeof right !== "object" || Array.isArray(left) || Array.isArray(right)) return false;
   const a = left as Record<string, unknown>, b = right as Record<string, unknown>;
   return Object.keys(a).length === Object.keys(b).length && Object.keys(a).every(key => Object.hasOwn(b, key) && snapshotValuesEqual(a[key], b[key]));
}

export const hasProposalChanges = (proposal: MutationProposal) => !snapshotValuesEqual(proposal.before, proposal.after);

const uuid = z.string().uuid();

type ReviewDirection = "inbound" | "outbound";

/**
 * The editor paths that review a proposal in place, canonical path first; empty when it has none.
 * Use cases are shared by both channels, so either Action editor edits the same Action.
 */
export function mutationProposalEditorPaths(proposal: MutationProposal): string[] {
   if (!uuid.safeParse(proposal.targetId).success) return [];
   if (proposal.capability === "use_cases_update") return [`/outbound/actions/${proposal.targetId}`, `/inbound/actions/${proposal.targetId}`];
   const direction = proposal.after.direction;
   if (proposal.capability !== "phone_scripts_update" || (direction !== "inbound" && direction !== "outbound")) return [];
   return [`/${direction}/scripts/${proposal.targetId}`];
}

/** The query that opens a proposal's review on its editor. */
export function mutationProposalReviewParams(proposal: MutationProposal, conversationId: string): URLSearchParams {
   return new URLSearchParams({proposalId: proposal.proposalId, conversationId});
}

/** The canonical editor that reviews the proposal, or undefined when it has none. */
export function mutationProposalHref(proposal: MutationProposal, conversationId?: string): string | undefined {
   const [path] = mutationProposalEditorPaths(proposal);
   if (!path || !conversationId || !uuid.safeParse(conversationId).success) return;
   return `${path}?${mutationProposalReviewParams(proposal, conversationId)}`;
}

export function scriptProposalHref(proposal: MutationProposal, conversationId?: string): string | undefined {
   return proposal.capability === "phone_scripts_update" ? mutationProposalHref(proposal, conversationId) : undefined;
}

/** Opens the proposal's own editor, or the original conversation when it has none. */
export function proposalReviewHref(proposal: MutationProposal | undefined, conversationId?: string | null): string | undefined {
   if (!conversationId || !uuid.safeParse(conversationId).success) return;
   return (proposal && mutationProposalHref(proposal, conversationId)) ?? `/gailgpt/${conversationId}`;
}

/** An editor page that can host a review, as read from its path. */
export type ReviewEditorPage = ProposalTarget & {direction: ReviewDirection};

const REVIEW_EDITOR_PATH = /^\/(inbound|outbound)\/(scripts?|actions)\/([a-fA-F0-9-]+)(?:\/(?:latest|draft|\d+))?$/;

/** The editor a path shows, including script revisions and either case of the target ID. */
export function reviewEditorPage(pathname: string | null): ReviewEditorPage | null {
   const match = REVIEW_EDITOR_PATH.exec(pathname ?? "");
   if (!match || !uuid.safeParse(match[3]).success) return null;
   return {
      capability: match[2] === "actions" ? "use_cases_update" : "phone_scripts_update",
      direction: match[1] === "inbound" ? "inbound" : "outbound",
      targetId: match[3],
   };
}

/** The page edits the proposal's target. An Action has no direction, so either channel matches. */
export function editsProposalTarget(page: ReviewEditorPage, proposal: MutationProposal): boolean {
   return sameProposalTarget(page, proposal) && (proposal.capability === "use_cases_update" || proposal.after.direction === page.direction);
}
